import { FileSystemAdapter } from '../utils/file-system-adapter.js';
import { IssueService } from '../services/issue-service.js';
import { PathnameService } from '../services/pathname-service.js';
import { loadLlmailConfig, LlmailConfig } from '../utils/config.js';
import { logger } from '../utils/logger.js';
import { minimatch } from 'minimatch';
import path from 'path';
import { load } from 'js-yaml';
import { FrontmatterMetadata } from '../types.js';
import { IntentService } from '../services/intent-service.js';
import { FileService } from '../services/file-service.js';
import { FileFormatValidator } from '../validation/file-format-validator.js';
import { MetadataFormatValidator } from '../validation/metadata-format-validator.js';
import { FileSystemValidator } from '../validation/file-system-validator.js';
import { Command } from 'commander';

export interface SyncOptions {
  include?: string;
  exclude?: string;
  dryRun?: boolean;    // Just show what would change
  force?: boolean;     // Skip confirmations
}

export interface SyncResult {
  changes: Array<{
    id: string;
    from: string;
    to: string;
    state: string;
  }>;
  errors?: Array<{
    id: string;
    error: string;
  }>;
}

/**
 * Checks if a file matches the include/exclude patterns
 */
function shouldProcessFile(filePath: string, options: SyncOptions): boolean {
  const relativePath = filePath.replace(/^issues\//, '');

  // Check exclude pattern first
  if (options.exclude && minimatch(relativePath, options.exclude)) {
    return false;
  }

  // If include pattern exists, file must match it
  if (options.include) {
    return minimatch(relativePath, options.include);
  }

  // No patterns or passed exclude check
  return true;
}

/**
 * Synchronizes file locations with their frontmatter metadata.
 * This ensures files are in the correct directories based on their state and type.
 */
export async function sync(fs: FileSystemAdapter, options: SyncOptions = {}): Promise<SyncResult> {
  const config = await loadLlmailConfig(fs);
  const intentService = new IntentService(config);
  const pathnameService = new PathnameService(config, intentService);
  const fileService = new FileService(fs, pathnameService);
  const fileFormatValidator = new FileFormatValidator();
  const metadataValidator = new MetadataFormatValidator();
  const fileSystemValidator = new FileSystemValidator(fs);
  const issueService = new IssueService(
    pathnameService,
    fs,
    intentService,
    fileService,
    fileFormatValidator,
    metadataValidator,
    fileSystemValidator
  );

  // Get all markdown files
  const files = await findAllMarkdownFiles(fs, config, options);
  if (files.length === 0) {
    return { changes: [] };  // No files found => empty changes
  }

  const changes: Array<{ id: string; from: string; to: string; state: string }> = [];
  const errors: Array<{ id: string; error: string }> = [];

  // Process each file
  for (const file of files) {
    try {
      const metadata = await issueService.frontmatterMethods.getFrontmatterMetadata(file);
      if (!metadata) {
        throw new Error(`No metadata found in file ${file}`);
      }

      const targetLocation = pathnameService.deriveFileLocation(metadata);
      const targetPath = path.join(targetLocation.dirname, targetLocation.basename);

      if (targetPath !== file) {
        if (!options.dryRun) {
          await issueService.fileMethods.moveFileWithMetadata(file, targetPath, metadata);
        }
        logger.success(`${options.dryRun ? '(dryRun) Would move' : 'Moved'} ${file} to ${targetPath}`);
        changes.push({
          id: metadata.id || path.basename(file),
          from: file,
          to: targetPath,
          state: metadata.state ?? ''
        });
      } else {
        logger.info(`File ${file} is already in the correct location`);
      }
    } catch (err) {
      errors.push({
        id: path.basename(file),
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }

  return { 
    changes,
    errors: errors.length ? errors : undefined
  };
}

// Helper functions
async function findAllMarkdownFiles(fs: FileSystemAdapter, config: LlmailConfig, options: SyncOptions): Promise<string[]> {
  const files: string[] = [];
  const rootDir = config.issues_dir;

  // Helper to check if a file should be included
  const shouldInclude = (file: string) => {
    if (!file.endsWith('.md')) return false;
    if (options.include && !minimatch(file, options.include)) return false;
    if (options.exclude && minimatch(file, options.exclude)) return false;
    return true;
  };

  // Helper to scan a directory recursively
  const scanDir = async (dir: string) => {
    const entries = await fs.readdir(dir);
    for (const entry of entries) {
      const fullPath = path.join(dir, entry);
      const stats = await fs.stat(fullPath);
      if (stats.isDirectory()) {
        await scanDir(fullPath);
      } else if (shouldInclude(fullPath)) {
        files.push(fullPath);
      }
    }
  };

  await scanDir(rootDir);
  return files;
}

async function parseFrontmatter(content: string): Promise<Record<string, any> | null> {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) {
    return null;
  }

  try {
    return load(match[1]) as Record<string, any>;
  } catch (err) {
    return null;
  }
}

export async function syncCommand(
  args: string[],
  fs: FileSystemAdapter,
  pathnameService: PathnameService
) {
  // Implementation of syncCommand function
} 